
import type { PropType } from 'vue'
import type { DispatcherWorkplaceCourier, DispatcherWorkplaceDataEntity, DispatcherWorkplaceDelivery, DispatcherWorkplaceTake, DispatcherWorkplaceItem, DispatcherWorkplaceCourierItem } from '@/modules/DispatcherWorkplaceModule/types'

import { computed, defineComponent, reactive } from 'vue'

import DwSidebarEntity from './Entity.vue'
import DwSidebarFilter from './Filter.vue'

export default defineComponent({
    components: {
        'dw-sidebar-entity': DwSidebarEntity,
        'dw-sidebar-filter': DwSidebarFilter
    },
    props: {
        courier: {
            type: Object as PropType<DispatcherWorkplaceCourier>,
            required: true
        },
        routeShowed: {
            type: Boolean,
            default: false
        },
        isCompletedOrdersEnabled: {
            type: Boolean,
            default: false,
        },
        isProblemsEnabled: {
            type: Boolean,
            default: false,
        },
        isDeliveriesEnabled: {
            type: Boolean,
            default: false,
        },
        isUndeliveredDeliveriesEnabled: {
            type: Boolean,
            default: false,
        },
        isTakesEnabled: {
            type: Boolean,
            default: false,
        },
        isUnassignedTakesEnabled: {
            type: Boolean,
            default: false,
        },
        selectedSidebarFeatureId: {
            type: String as PropType<string | null>,
            default: null
        }
    },
    emits: ['show-route', 'entity-clicked'],
    setup(props, { emit }) {
        const state = reactive({
            is_opened: false,
        })

        const toggle = () => {
            state.is_opened = !state.is_opened
        }

        const onShowRoute = () => {
            emit('show-route', { ...props.courier })
        }

        const onEntityClicked = (dataEntity: DispatcherWorkplaceDataEntity) => {
            emit('entity-clicked', dataEntity)
        }

        const isTakeCompleted = (entity: DispatcherWorkplaceItem) => {
            if (entity.type === 1) {
                return entity.state === 1 || entity.statusId === 3
            }

            return false
        }

        const isDeliveryCompleted = (entity: DispatcherWorkplaceItem) => {
            if (entity.type === 2) {
                return entity.state === 1
            }

            return false
        }

        const isFeatureEntityCompleted = (entity: DispatcherWorkplaceItem) => {
            if (entity.type === 1) {
                return isTakeCompleted(entity)
            }

            if (entity.type === 2) {
                return isDeliveryCompleted(entity)
            }

            return false
        }

        const sortOrders = (orders: DispatcherWorkplaceDataEntity[]): DispatcherWorkplaceDataEntity[] => {
            return orders.sort((a, b) => {
                const getPriority = (order: DispatcherWorkplaceDataEntity): number => {
                    if (order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned) return 0 // Неназначенные заборы
                    if (order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered) return 1 // Невыданные доставки
                    if (!isFeatureEntityCompleted(order)) return 2 // Незавершённые
                    return 3 // Завершённые
                }

                return getPriority(a) - getPriority(b)
            })
        }

        const sortByActionSequence = (a: DispatcherWorkplaceCourierItem, b: DispatcherWorkplaceCourierItem) => {
            if (a.actionSequence === null && b.actionSequence === null) return 0
            if (a.actionSequence === null) return 1
            if (b.actionSequence === null) return -1

            return a.actionSequence - b.actionSequence
        }

        const computedOrders = computed(() => {
            let completedTakesAndDeliveries: DispatcherWorkplaceDataEntity[] = []
            let ordersToRender: DispatcherWorkplaceDataEntity[] = []

            const completedTakes = props.courier.items.filter(isTakeCompleted).sort(sortByActionSequence)
            const completedDeliveries = props.courier.items.filter(isDeliveryCompleted).sort(sortByActionSequence)

            const takes = props.courier.items.filter((order) => order.type === 1 && !((order as DispatcherWorkplaceTake).notAssigned) && !isFeatureEntityCompleted(order))
            const deliveries = props.courier.items.filter((order) => order.type === 2 && !((order as DispatcherWorkplaceDelivery).notDelivered) && !isFeatureEntityCompleted(order))

            const unassigned = props.courier.items.filter((order) => order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned)
            const undelivered = props.courier.items.filter((order) => order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered)

            ordersToRender = ordersToRender.concat(
                props.courier.items.filter((order) => {
                    const isCompletedTake = isTakeCompleted(order)
                    const isCompletedDelivery = isDeliveryCompleted(order)

                    const isTake = order.type === 1 && !(order as DispatcherWorkplaceTake).notAssigned && !isFeatureEntityCompleted(order)
                    const isDelivery = order.type === 2 && !(order as DispatcherWorkplaceDelivery).notDelivered && !isFeatureEntityCompleted(order)
                    const isUnassignedTake = order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned
                    const isUndeliveredDelivery = order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered
                    return !isCompletedTake && !isCompletedDelivery && !isTake && !isDelivery && !isUnassignedTake && !isUndeliveredDelivery
                })
            )

            if (props.isCompletedOrdersEnabled) {
                if (props.isTakesEnabled) {
                    completedTakesAndDeliveries = completedTakesAndDeliveries.concat(completedTakes)
                }

                if (props.isDeliveriesEnabled) {
                    completedTakesAndDeliveries = completedTakesAndDeliveries.concat(completedDeliveries)
                }
            } else {
                if (props.isTakesEnabled) {
                    ordersToRender = ordersToRender.concat(takes)
                }

                if (props.isDeliveriesEnabled) {
                    ordersToRender = ordersToRender.concat(deliveries)
                }
            }
    
            if (props.isUnassignedTakesEnabled) {
                ordersToRender = ordersToRender.concat(unassigned)
            }
    
            if (props.isUndeliveredDeliveriesEnabled) {
                ordersToRender = ordersToRender.concat(undelivered)
            }

            const sorteredCompletedOrders = completedTakesAndDeliveries.sort(sortByActionSequence)
            const sorteredUncompletedOrders = sortOrders(ordersToRender)

            return [...sorteredCompletedOrders, ...sorteredUncompletedOrders]
        })

        return {
            state,
            toggle,
            onShowRoute,
            onEntityClicked,
            computedOrders,
        }
    }
})
